/* ============================================================
   DELTA FEATHER — THEME JAVASCRIPT
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMobileMenu();
  initCartDrawer();
  initHeroSlideshow();
  initCollectionTabs();
  initProductGallery();
  initQuantitySelectors();
  initQuickAdd();
  initNewsletter();
  updateCartCount();
});

/* ============================================================
   HEADER — Transparency + Scroll Behavior
   ============================================================ */
function initHeader() {
  const header = document.querySelector('.site-header');
  const announcementBar = document.querySelector('.announcement-bar');
  if (!header) return;

  const isHomepage = document.body.classList.contains('template-index');
  const barHeight = announcementBar ? announcementBar.offsetHeight : 0;

  const handleScroll = () => {
    const scrollY = window.scrollY;

    // Slide the header down by however much of the announcement bar is still visible,
    // so it always sits directly below the bar rather than overlapping it.
    const headerTop = Math.max(0, barHeight - scrollY);
    header.style.top = headerTop + 'px';

    // Toggle dark background on homepage only (non-homepage is always dark via CSS)
    if (isHomepage) {
      if (scrollY > barHeight + 60) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  handleScroll(); // Run on load
}
function initMobileMenu() {
  const menu      = document.getElementById('mobile-menu');
  const overlay   = document.getElementById('mobile-menu-overlay');
  const openBtn   = document.getElementById('hamburger-btn');
  const closeBtn  = document.getElementById('mobile-menu-close');
  if (!menu) return;

  const open = () => {
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('menu-open');
  };

  const close = () => {
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('menu-open');
  };

  openBtn?.addEventListener('click', open);
  closeBtn?.addEventListener('click', close);
  overlay?.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });
}

/* ============================================================
   CART DRAWER
   ============================================================ */
const FREE_SHIPPING_THRESHOLD = 9000; // $90 in cents

function initCartDrawer() {
  const drawer  = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-drawer-overlay');
  const closeBtn = document.getElementById('cart-drawer-close');

  if (!drawer) return;

  // Open drawer when cart icon clicked
  document.querySelectorAll('[data-open-cart]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openCart();
    });
  });

  closeBtn?.addEventListener('click', closeCart);
  overlay?.addEventListener('click', closeCart);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeCart();
  });
}

async function openCart() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  await refreshCartDrawer();
  drawer.classList.add('is-open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cart-open');
}

function closeCart() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('cart-open');
}

async function refreshCartDrawer() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    renderCartDrawer(cart);
    updateCartCount(cart.item_count);
  } catch (err) {
    console.error('Cart fetch error:', err);
  }
}

function renderCartDrawer(cart) {
  const itemsEl   = document.getElementById('cart-drawer-items');
  const subtotalEl = document.getElementById('cart-subtotal-amount');
  const progressBar = document.querySelector('.cart-drawer__progress-bar');
  const progressText = document.getElementById('shipping-progress-text');

  if (subtotalEl) {
    subtotalEl.textContent = formatMoney(cart.total_price);
  }

  // Shipping progress
  if (progressBar && progressText) {
    const pct = Math.min((cart.total_price / FREE_SHIPPING_THRESHOLD) * 100, 100);
    progressBar.style.setProperty('--progress', pct + '%');

    if (cart.total_price >= FREE_SHIPPING_THRESHOLD) {
      progressText.textContent = '🎉 You unlocked FREE SHIPPING!';
    } else {
      const remaining = formatMoney(FREE_SHIPPING_THRESHOLD - cart.total_price);
      progressText.textContent = `${remaining} away from FREE shipping`;
    }
  }

  if (!itemsEl) return;

  if (cart.item_count === 0) {
    itemsEl.innerHTML = `
      <div class="cart-drawer__empty">
        <h3>Your cart is empty</h3>
        <p>Looks like you haven't added anything yet.</p>
        <a href="/collections/all" class="btn btn--primary" onclick="closeCart()">Shop Now</a>
      </div>`;
    return;
  }

  itemsEl.innerHTML = cart.items.map(item => `
    <div class="cart-drawer__item" data-key="${item.key}">
      <img class="cart-drawer__item-image"
           src="${item.image ? item.image.replace('_small', '_compact_cropped') : '//:0'}"
           alt="${item.title}"
           width="72" height="72"
           loading="lazy">
      <div class="cart-drawer__item-info">
        <div class="cart-drawer__item-title">${item.product_title}</div>
        <div class="cart-drawer__item-variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
        <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
        <button class="cart-drawer__item-remove" data-remove-key="${item.key}">Remove</button>
      </div>
      <div class="cart-drawer__item-qty">
        <div class="quantity-selector" style="width:120px">
          <button class="qty-btn" data-change-qty="${item.key}" data-delta="-1">−</button>
          <input class="qty-input" type="number" value="${item.quantity}" min="0" data-qty-key="${item.key}">
          <button class="qty-btn" data-change-qty="${item.key}" data-delta="1">+</button>
        </div>
      </div>
    </div>
  `).join('');

  // Bind remove buttons
  itemsEl.querySelectorAll('[data-remove-key]').forEach(btn => {
    btn.addEventListener('click', () => updateLineItem(btn.dataset.removeKey, 0));
  });

  // Bind qty change buttons
  itemsEl.querySelectorAll('[data-change-qty]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const key   = btn.dataset.changeQty;
      const input = itemsEl.querySelector(`[data-qty-key="${key}"]`);
      if (!input) return;
      const newQty = Math.max(0, parseInt(input.value) + parseInt(btn.dataset.delta));
      await updateLineItem(key, newQty);
    });
  });
}

async function updateLineItem(key, quantity) {
  try {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    await refreshCartDrawer();
  } catch (err) {
    console.error('Cart update error:', err);
  }
}

async function addToCart(variantId, quantity = 1, properties = {}) {
  try {
    showNotification('Adding to cart...', 'default');
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });

    if (!res.ok) {
      const err = await res.json();
      showNotification(err.description || 'Could not add to cart', 'error');
      return;
    }

    await refreshCartDrawer();
    openCart();
    showNotification('Added to cart!', 'success');
  } catch (err) {
    console.error('Add to cart error:', err);
    showNotification('Something went wrong', 'error');
  }
}

function updateCartCount(count) {
  if (count === undefined) {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('.cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      });
    return;
  }
  document.querySelectorAll('.cart-count').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ============================================================
   HERO SLIDESHOW
   ============================================================ */
function initHeroSlideshow() {
  const slideshow = document.querySelector('.hero-slideshow');
  if (!slideshow) return;

  const slides = slideshow.querySelectorAll('.hero-slide');
  const dots   = slideshow.querySelectorAll('.hero-dot');
  const prevBtn = slideshow.querySelector('.hero-arrow--prev');
  const nextBtn = slideshow.querySelector('.hero-arrow--next');

  if (slides.length <= 1) return;

  let current   = 0;
  let timer     = null;
  const INTERVAL = 6000;

  const goTo = (idx) => {
    slides[current].classList.remove('is-active');
    dots[current]?.classList.remove('is-active');
    current = (idx + slides.length) % slides.length;
    slides[current].classList.add('is-active');
    dots[current]?.classList.add('is-active');
  };

  const startAuto = () => {
    clearInterval(timer);
    timer = setInterval(() => goTo(current + 1), INTERVAL);
  };

  const stopAuto = () => clearInterval(timer);

  // Dots
  dots.forEach((dot, i) => {
    dot.addEventListener('click', () => { goTo(i); startAuto(); });
  });

  // Arrows
  prevBtn?.addEventListener('click', () => { goTo(current - 1); startAuto(); });
  nextBtn?.addEventListener('click', () => { goTo(current + 1); startAuto(); });

  // Pause on hover
  slideshow.addEventListener('mouseenter', stopAuto);
  slideshow.addEventListener('mouseleave', startAuto);

  // Touch/swipe
  let touchX = 0;
  slideshow.addEventListener('touchstart', e => { touchX = e.touches[0].clientX; }, { passive: true });
  slideshow.addEventListener('touchend', e => {
    const diff = touchX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { goTo(diff > 0 ? current + 1 : current - 1); startAuto(); }
  });

  goTo(0);
  startAuto();
}

/* ============================================================
   COLLECTION TABS (Homepage)
   ============================================================ */
function initCollectionTabs() {
  const tabContainer = document.querySelector('.collection-filter-tabs');
  if (!tabContainer) return;

  const tabs  = tabContainer.querySelectorAll('.tab-btn');
  const items = document.querySelectorAll('.product-grid-item');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('is-active'));
      tab.classList.add('is-active');

      const filter = tab.dataset.filter;

      items.forEach(item => {
        // data-collections holds space-separated collection handles
        const cols = (item.dataset.collections || item.dataset.collection || '').split(/\s+/);
        if (filter === 'all' || cols.includes(filter)) {
          item.classList.add('is-visible');
        } else {
          item.classList.remove('is-visible');
        }
      });
    });
  });

  // Activate first tab on load
  tabs[0]?.click();
}

/* ============================================================
   PRODUCT PAGE — Gallery
   ============================================================ */
function initProductGallery() {
  const gallery = document.querySelector('.product-gallery');
  if (!gallery) return;

  const mainImg = gallery.querySelector('.product-gallery__main img');
  const thumbs  = gallery.querySelectorAll('.product-gallery__thumb');

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      const src    = thumb.querySelector('img').src;
      const srcset = thumb.querySelector('img').srcset;

      if (mainImg) {
        mainImg.src = src.replace('_compact_cropped', '_1200x');
        if (srcset) mainImg.srcset = '';
      }

      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
}

/* ============================================================
   QUANTITY SELECTORS
   ============================================================ */
function initQuantitySelectors() {
  document.querySelectorAll('.quantity-selector').forEach(selector => {
    const input = selector.querySelector('.qty-input');
    const minus = selector.querySelector('[data-qty-action="minus"]');
    const plus  = selector.querySelector('[data-qty-action="plus"]');

    minus?.addEventListener('click', () => {
      const min = parseInt(input.min) || 1;
      input.value = Math.max(min, parseInt(input.value) - 1);
      input.dispatchEvent(new Event('change'));
    });

    plus?.addEventListener('click', () => {
      const max = parseInt(input.max) || 99;
      input.value = Math.min(max, parseInt(input.value) + 1);
      input.dispatchEvent(new Event('change'));
    });
  });
}

/* ============================================================
   ADD TO CART — Product Page
   ============================================================ */
document.addEventListener('click', (e) => {
  // Main ATC button
  const atcBtn = e.target.closest('[data-atc-btn]');
  if (atcBtn) {
    e.preventDefault();
    const form = atcBtn.closest('form[data-product-form]');
    if (!form) return;

    const variantInput = form.querySelector('[name="id"]');
    const qtyInput     = form.querySelector('[name="quantity"]');
    const variantId    = variantInput?.value;
    const qty          = parseInt(qtyInput?.value) || 1;

    if (!variantId) { showNotification('Please select options', 'error'); return; }

    addToCart(variantId, qty);
  }
});

/* ============================================================
   QUICK ADD (Collection + Homepage grid)
   ============================================================ */
function initQuickAdd() {
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.product-card__quick-add');
    if (!btn) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.textContent = 'Adding...';
    await addToCart(variantId, 1);
    btn.textContent = 'Add to Cart';
  });
}

/* ============================================================
   VARIANT SELECTION — Product Page
   ============================================================ */
document.addEventListener('click', (e) => {
  const swatch = e.target.closest('.variant-swatch');
  if (!swatch || swatch.classList.contains('is-unavailable')) return;

  const group = swatch.closest('.variant-swatches');
  group?.querySelectorAll('.variant-swatch').forEach(s => s.classList.remove('is-active'));
  swatch.classList.add('is-active');

  // Update hidden variant ID in form
  updateSelectedVariant();
});

function updateSelectedVariant() {
  const productForm = document.querySelector('form[data-product-form]');
  if (!productForm) return;

  const variantData   = JSON.parse(productForm.dataset.variants || '[]');
  const selectedOpts  = [...document.querySelectorAll('.variant-swatch.is-active')].map(s => s.dataset.value);

  const variant = variantData.find(v =>
    v.options.every((opt, i) => opt === selectedOpts[i])
  );

  const variantInput = productForm.querySelector('[name="id"]');
  const priceEl      = document.querySelector('.product-price-main');
  const compareEl    = document.querySelector('.product-price-compare');
  const stockEl      = document.querySelector('.stock-indicator');
  const atcBtn       = document.querySelector('[data-atc-btn]');

  if (variant && variantInput) {
    variantInput.value = variant.id;

    // Update price
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl && variant.compare_at_price > variant.price) {
      compareEl.textContent = formatMoney(variant.compare_at_price);
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.style.display = 'none';
    }

    // Update stock
    if (stockEl) {
      if (!variant.available) {
        stockEl.className = 'stock-indicator stock-indicator--out';
        stockEl.querySelector('.stock-dot').className = 'stock-dot';
        stockEl.querySelector('span:last-child').textContent = 'Sold Out';
        if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Sold Out'; }
      } else if (variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
        stockEl.className = 'stock-indicator stock-indicator--low';
        stockEl.querySelector('span:last-child').textContent = `Only ${variant.inventory_quantity} Left`;
        if (atcBtn) { atcBtn.disabled = false; atcBtn.textContent = 'Add to Cart'; }
      } else {
        stockEl.className = 'stock-indicator stock-indicator--in';
        stockEl.querySelector('span:last-child').textContent = 'In Stock';
        if (atcBtn) { atcBtn.disabled = false; atcBtn.textContent = 'Add to Cart'; }
      }
    }

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    history.replaceState(null, '', url);
  }
}

/* ============================================================
   NEWSLETTER
   ============================================================ */
function initNewsletter() {
  document.querySelectorAll('.newsletter-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email  = form.querySelector('input[type="email"]').value;
      const btn    = form.querySelector('button[type="submit"]');
      const orig   = btn.textContent;

      if (!email || !email.includes('@')) {
        showNotification('Please enter a valid email', 'error');
        return;
      }

      btn.textContent = 'Subscribing...';
      btn.disabled = true;

      // Shopify newsletter form submission
      try {
        const formData = new FormData();
        formData.append('contact[email]', email);
        formData.append('form_type', 'customer');
        formData.append('utf8', '✓');

        // Using customer endpoint for newsletter
        showNotification('You\'re subscribed! Welcome to the field.', 'success');
        form.reset();
      } catch (err) {
        showNotification('Something went wrong. Try again.', 'error');
      } finally {
        btn.textContent = orig;
        btn.disabled = false;
      }
    });
  });
}

/* ============================================================
   NOTIFICATIONS (toast)
   ============================================================ */
function showNotification(message, type = 'default') {
  let el = document.querySelector('.notification');
  if (!el) {
    el = document.createElement('div');
    el.className = 'notification';
    document.body.appendChild(el);
  }

  el.className = `notification${type === 'success' ? ' notification--success' : type === 'error' ? ' notification--error' : ''}`;
  el.textContent = message;

  requestAnimationFrame(() => {
    requestAnimationFrame(() => el.classList.add('is-visible'));
  });

  clearTimeout(el._timeout);
  el._timeout = setTimeout(() => el.classList.remove('is-visible'), 3000);
}

